// Approval transitions for Generator–Consumer Mapping.
//
// The rule the business asked for: a mapping is submitted for approval, and
// ONLY an Admin may approve or reject it. That authority check lives here so
// the list, the detail page and the editor can't drift apart on who is allowed
// to do what.
import { message } from 'antd'
import { authStore } from '@/context/auth'
import { mappingStore, approvalOf, type GeneratorMapping } from './mappingStore'
import { RECIPIENT_ADMIN } from '@/pages/Notification/notificationStore'
import { NotificationEngine } from '@/utils/notificationEngine'

import { hasModulePermission } from '@/utils/permissions'
import type { Role } from '@/context/MasterDataContext'

/** Human label for a mapping in notification text. */
const describe = (m?: GeneratorMapping): string =>
  m ? `${m.generatorName}${m.plantName ? ` — ${m.plantName}` : ''}` : 'a generator–consumer mapping'

/** Admin or user with Mapping approve permission signs off a mapping. */
export const canApproveMappings = (roles?: Role[]): boolean => {
  const user = authStore.getUser()
  if (!user) return false
  if (authStore.isAdmin()) return true
  return !!roles && hasModulePermission(user, roles, 'Mapping', 'approve')
}

/** A mapping waiting on an admin decision. */
export const isAwaitingApproval = (m: GeneratorMapping): boolean => approvalOf(m) === 'pending'

/** Draft and rejected mappings can be (re)submitted; pending/approved cannot. */
export const canSubmitForApproval = (m: GeneratorMapping): boolean =>
  ['draft', 'rejected'].includes(approvalOf(m))

const actor = (): string | null => {
  const name = authStore.getUser()?.name
  if (!name) {
    message.error('Your session looks stale — please refresh and sign in again.')
    return null
  }
  return name
}

/**
 * Tell whoever submitted the mapping what the admin decided.
 * No-op when the row predates the approval flow and has no submitter, or when
 * the admin is approving their own submission (they already know).
 */
const notifySubmitter = (
  mapping: GeneratorMapping | undefined,
  payload: { event: 'SYSTEM_ALERT' | 'APPROVAL_REJECTED'; title: string; message: string },
) => {
  const to = mapping?.submittedBy
  if (!to || to === authStore.getUser()?.name) return
  NotificationEngine.trigger({ ...payload, recipient: to, channels: ['In-App'] })
}

export function submitForApproval(id: string): boolean {
  const who = actor()
  if (!who) return false
  const mapping = mappingStore.get(id)
  mappingStore.save({
    id,
    approvalStatus: 'pending',
    submittedBy: who,
    submittedAt: new Date().toISOString(),
    // Clear any prior rejection so the record doesn't show a stale reason.
    approvedBy: null,
    approvedAt: null,
    approvalComments: null,
  } as Partial<GeneratorMapping> as GeneratorMapping)

  // Addressed to 'Admin' so only admin users see it — mapping approval is
  // admin-only, so anyone else being paged about it could do nothing anyway.
  NotificationEngine.trigger({
    event: 'ALLOCATION_PENDING',
    title: `Mapping Approval Required: ${describe(mapping)}`,
    message: `${who} submitted the generator–consumer mapping for ${describe(mapping)} (${mapping?.consumers?.length ?? 0} consumer(s), ${mapping?.totalAllocated ?? 0}% allocated). Only an Admin can approve or reject it.|||MAPPING_ID:${id}`,
    recipient: RECIPIENT_ADMIN,
    channels: ['In-App'],
  })

  message.success('Mapping submitted for approval. An admin has been notified.')
  return true
}

export function approveMapping(id: string, comments?: string): boolean {
  if (!canApproveMappings()) {
    message.error('Only an Admin can approve a generator–consumer mapping.')
    return false
  }
  const who = actor()
  if (!who) return false
  const mapping = mappingStore.get(id)
  mappingStore.save({
    id,
    approvalStatus: 'approved',
    approvedBy: who,
    approvedAt: new Date().toISOString(),
    approvalComments: comments ?? null,
  } as Partial<GeneratorMapping> as GeneratorMapping)

  notifySubmitter(mapping, {
    event: 'SYSTEM_ALERT',
    title: `Mapping Approved: ${describe(mapping)}`,
    message: `${who} approved the generator–consumer mapping for ${describe(mapping)}.|||MAPPING_ID:${id}`,
  })

  message.success('Mapping approved.')
  return true
}

export function rejectMapping(id: string, reason: string): boolean {
  if (!canApproveMappings()) {
    message.error('Only an Admin can reject a generator–consumer mapping.')
    return false
  }
  if (!reason.trim()) {
    message.error('Please give a reason for rejecting this mapping.')
    return false
  }
  const who = actor()
  if (!who) return false
  const mapping = mappingStore.get(id)
  mappingStore.save({
    id,
    approvalStatus: 'rejected',
    approvedBy: who,
    approvedAt: new Date().toISOString(),
    approvalComments: reason.trim(),
  } as Partial<GeneratorMapping> as GeneratorMapping)

  notifySubmitter(mapping, {
    event: 'APPROVAL_REJECTED',
    title: `Mapping Rejected: ${describe(mapping)}`,
    message: `${who} rejected the generator–consumer mapping for ${describe(mapping)}. Reason: ${reason.trim()}. Revise the allocation and resubmit.|||MAPPING_ID:${id}`,
  })

  message.success('Mapping rejected — the submitter has been notified.')
  return true
}
